<template>
  <div>
    <div
      class="d-flex justify-content-between align-items-center mb-3 flex-wrap"
    >
      <a-button type="primary" icon="plus" @click="openModal(null)">
        {{ $t("action.ajouter") }}
      </a-button>
    </div>
    <a-table
      row-key="_id"
      :loading="loading"
      :columns="columns"
      :data-source="rows"
      :pagination="{ pageSize: 10 }"
      :scroll="{ x: 'max-content' }"
    >
      <template slot="atype" slot-scope="text">
        {{
          text === "external"
            ? $t("comptabilite.associateExternal")
            : $t("comptabilite.associateInternal")
        }}
      </template>
      <template slot="accountingClass" slot-scope="text, record">
        <a-tag v-if="record.accountingClass" color="geekblue">
          {{ record.accountingClass }}
        </a-tag>
        <a-button
          v-else
          size="small"
          type="link"
          @click="openAssignModal(record)"
        >
          {{ $t("comptabilite.accountingClassAssign") }}
        </a-button>
      </template>
      <template slot="action" slot-scope="text, record">
        <div class="editable-row-operations">
          <a-button type="primary" size="small" @click="openModal(record)">
            {{ $t("action.modifier") }}
          </a-button>
          <a-button type="danger" size="small" class="ml-2">
            <a-popconfirm
              :title="$t('requis.supp')"
              @confirm="() => remove(record._id)"
            >
              <a>{{ $t("action.supprimer") }}</a>
            </a-popconfirm>
          </a-button>
        </div>
      </template>
    </a-table>

    <a-modal
      :visible="modalVisible"
      :title="editingId ? $t('action.modifier') : $t('action.ajouter')"
      :confirm-loading="saving"
      :width="640"
      @ok="onModalOk"
      @cancel="closeModal"
      destroy-on-close
      wrap-class-name="parametrage-compact-modal"
    >
      <a-form :form="form" layout="vertical" class="compact-param-form">
        <div class="row">
          <div class="col-md-6">
            <a-form-item :label="$t('comptabilite.fullName')">
              <a-input
                v-decorator="[
                  'fullName',
                  { rules: [{ required: true, message: $t('requis.nom') }] },
                ]"
              />
            </a-form-item>
          </div>
          <div class="col-md-6">
            <a-form-item :label="$t('comptabilite.associateType')">
              <a-select
                v-decorator="[
                  'associateType',
                  {
                    initialValue: 'internal',
                    rules: [{ required: true }],
                  },
                ]"
              >
                <a-select-option value="internal">{{
                  $t("comptabilite.associateInternal")
                }}</a-select-option>
                <a-select-option value="external">{{
                  $t("comptabilite.associateExternal")
                }}</a-select-option>
              </a-select>
            </a-form-item>
          </div>
        </div>
        <div class="row">
          <div class="col-md-6">
            <a-form-item :label="$t('comptabilite.email')">
              <a-input
                v-decorator="['email', { initialValue: '' }]"
                type="email"
              />
            </a-form-item>
          </div>
          <div class="col-md-6">
            <a-form-item :label="$t('comptabilite.telephone')">
              <phone-number-input
                default-country-code="TN"
                v-decorator="['phone']"
              />
            </a-form-item>
          </div>
        </div>
        <div v-if="!editingId" class="row">
          <div class="col-md-12">
            <a-form-item :label="$t('comptabilite.accountingClassReference')">
              <plan-comptable-select v-decorator="['accountingParentClass']" />
              <div class="text-muted small">
                {{ $t("comptabilite.accountingClassChildHint") }}
              </div>
            </a-form-item>
          </div>
        </div>
        <div v-else class="row">
          <div class="col-md-6">
            <a-form-item :label="$t('comptabilite.accountingClass')">
              <a-input :value="currentChildClass" disabled />
            </a-form-item>
          </div>
          <div class="col-md-6">
            <a-form-item :label="$t('comptabilite.accountingParentClass')">
              <a-input :value="currentParentClass" disabled />
            </a-form-item>
          </div>
        </div>
      </a-form>
    </a-modal>

    <a-modal
      :visible="assignVisible"
      :title="$t('comptabilite.accountingClassAssign')"
      :confirm-loading="assignSaving"
      :width="520"
      @ok="onAssignOk"
      @cancel="closeAssignModal"
      destroy-on-close
    >
      <a-form layout="vertical">
        <a-form-item :label="$t('comptabilite.accountingClassReference')">
          <plan-comptable-select v-model="assignClass" />
          <div class="text-muted small">
            {{ $t("comptabilite.accountingClassChildHint") }}
          </div>
        </a-form-item>
      </a-form>
    </a-modal>
  </div>
</template>

<script>
import apiClient from "@/services/axios";
import { showApiError } from "@/utilities/showApiError";
import PlanComptableSelect from "@/views/compV3/decaissement/components/PlanComptableSelect.vue";

export default {
  name: "AssociatesTab",
  components: { PlanComptableSelect },
  beforeMount() {
    this.form = this.$form.createForm(this);
  },
  data() {
    return {
      loading: false,
      rows: [],
      modalVisible: false,
      saving: false,
      editingId: null,
      currentChildClass: "",
      currentParentClass: "",
      assignVisible: false,
      assignSaving: false,
      assignTarget: null,
      assignClass: "",
    };
  },
  computed: {
    columns() {
      return [
        {
          title: this.$t("comptabilite.fullName"),
          dataIndex: "fullName",
          key: "fullName",
        },
        {
          title: this.$t("comptabilite.associateType"),
          dataIndex: "associateType",
          key: "associateType",
          scopedSlots: { customRender: "atype" },
        },
        {
          title: this.$t("comptabilite.accountingClass"),
          dataIndex: "accountingClass",
          key: "accountingClass",
          scopedSlots: { customRender: "accountingClass" },
        },
        {
          title: this.$t("comptabilite.email"),
          dataIndex: "email",
          key: "email",
          ellipsis: true,
        },
        {
          title: this.$t("comptabilite.telephone"),
          dataIndex: "phone",
          key: "phone",
        },
        {
          title: this.$t("action.actions"),
          key: "action",
          fixed: "right",
          scopedSlots: { customRender: "action" },
        },
      ];
    },
  },
  created() {
    this.load();
  },
  methods: {
    async load() {
      this.loading = true;
      try {
        const res = await apiClient.get("/compV3/depense/associates");
        this.rows = res.data || [];
      } catch (e) {
        this.$message.error(this.$t("warning.connectionProblem"));
      } finally {
        this.loading = false;
      }
    },
    openModal(record) {
      this.editingId = record ? record._id : null;
      this.currentChildClass = (record && record.accountingClass) || "—";
      this.currentParentClass = (record && record.accountingParentClass) || "—";
      this.modalVisible = true;
      this.$nextTick(() => {
        if (record) {
          this.form.setFieldsValue({
            fullName: record.fullName,
            associateType: record.associateType || "internal",
            email: record.email || "",
            phone: record.phone || "",
          });
        } else {
          this.form.resetFields();
          this.form.setFieldsValue({
            associateType: "internal",
            email: "",
            phone: "",
          });
        }
      });
    },
    closeModal() {
      this.modalVisible = false;
      this.editingId = null;
      this.currentChildClass = "";
      this.currentParentClass = "";
    },
    onModalOk(e) {
      e.preventDefault();
      this.submit();
    },
    submit() {
      this.form.validateFields((err, values) => {
        if (err) return;
        const phone = (values.phone || "").trim();
        if (phone) {
          const digits = phone.replace(/\D/g, "");
          if (phone.startsWith("+216") && digits.length !== 11) {
            this.$message.error(this.$t("requis.telephone"));
            return;
          }
          if (phone.startsWith("+254") && digits.length !== 13) {
            this.$message.error(this.$t("requis.telephoneKE"));
            return;
          }
        }
        this.saving = true;
        const data = { ...values };
        if (this.editingId) {
          delete data.accountingParentClass;
        } else if (!data.accountingParentClass) {
          delete data.accountingParentClass;
        }
        const payload = { data };
        const req = this.editingId
          ? apiClient.patch(
              `/compV3/depense/associates/${this.editingId}`,
              payload
            )
          : apiClient.put("/compV3/depense/associates", payload);
        req
          .then(() => {
            this.$message.success(this.$t("success.operationEffectue"));
            this.closeModal();
            this.load();
          })
          .catch((e) => showApiError(this, e))
          .finally(() => {
            this.saving = false;
          });
      });
    },
    openAssignModal(record) {
      this.assignTarget = record;
      this.assignClass = "";
      this.assignVisible = true;
    },
    closeAssignModal() {
      this.assignVisible = false;
      this.assignTarget = null;
      this.assignClass = "";
    },
    onAssignOk() {
      if (!this.assignTarget || !this.assignClass) {
        this.$message.warning(
          this.$t("comptabilite.accountingClassParentMissingShort")
        );
        return;
      }
      this.assignSaving = true;
      apiClient
        .patch(
          `/compV3/depense/associates/${this.assignTarget._id}/assign-class`,
          { data: { accountingParentClass: this.assignClass } }
        )
        .then(() => {
          this.$message.success(this.$t("success.operationEffectue"));
          this.closeAssignModal();
          this.load();
        })
        .catch((e) => showApiError(this, e))
        .finally(() => {
          this.assignSaving = false;
        });
    },
    remove(id) {
      apiClient
        .patch(`/compV3/depense/associates/${id}`, {
          data: { status: "inactive" },
        })
        .then(() => {
          this.$message.success(this.$t("success.operationEffectue"));
          this.load();
        })
        .catch((e) => showApiError(this, e));
    },
  },
};
</script>
<style scoped>
.compact-param-form >>> .ant-form-item {
  margin-bottom: 10px;
}
</style>
